export class SettlementContractError extends Error {
    constructor(message) {
        super(message);
        this.name = "SettlementContractError";
        this.kind = "contract";
    }
}

export const SETTLEMENT_STATUSES = Object.freeze([
    "generated",
    "draft",
    "approved",
    "exported",
    "sent_to_bank",
    "partially_paid",
    "paid",
    "cancelled",
]);

export const SETTLEMENT_READINESS_STATUSES = Object.freeze([
    "ready",
    "missing_requisites",
    "missing_documents",
    "needs_review",
    "on_hold",
]);

const MONEY_PATTERN = /^-?(?:0|[1-9]\d*)\.\d{2}$/;
const DECIMAL_4_PATTERN = /^-?(?:0|[1-9]\d*)\.\d{4}$/;
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const object = (value, label) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new SettlementContractError(`${label} must be an object`);
    }
    return value;
};

const field = (value, key, label = key) => {
    if (!hasOwn(value, key)) throw new SettlementContractError(`${label} is required`);
    return value[key];
};

const string = (value, label) => {
    if (typeof value !== "string") throw new SettlementContractError(`${label} must be text`);
    return value;
};

const nonEmptyString = (value, label) => {
    const result = string(value, label);
    if (!result.trim()) throw new SettlementContractError(`${label} must not be empty`);
    return result;
};

const positiveInteger = (value, label) => {
    const number = typeof value === "number" ? value : Number(value);
    if (!Number.isInteger(number) || number <= 0) {
        throw new SettlementContractError(`${label} must be a positive integer`);
    }
    return number;
};

const nullablePositiveInteger = (value, label) =>
    value === null ? null : positiveInteger(value, label);

const nonNegativeInteger = (value, label) => {
    const number = typeof value === "number" ? value : Number(value);
    if (!Number.isInteger(number) || number < 0) {
        throw new SettlementContractError(`${label} must be a non-negative integer`);
    }
    return number;
};

const money = (value, label) => {
    if (typeof value !== "string" || !MONEY_PATTERN.test(value)) {
        throw new SettlementContractError(`${label} must be a canonical decimal money string`);
    }
    return value;
};

const decimal4 = (value, label) => {
    if (typeof value !== "string" || !DECIMAL_4_PATTERN.test(value)) {
        throw new SettlementContractError(`${label} must be a four-place decimal string`);
    }
    return value;
};

const nullableTimestamp = (value, label) => {
    if (value === null) return null;
    if (
        typeof value !== "string" ||
        !ISO_TIMESTAMP_PATTERN.test(value) ||
        Number.isNaN(Date.parse(value))
    ) {
        throw new SettlementContractError(`${label} must be an ISO timestamp or null`);
    }
    return value;
};

const timestamp = (value, label) => {
    const result = nullableTimestamp(value, label);
    if (result === null) throw new SettlementContractError(`${label} must not be null`);
    return result;
};

const enumValue = (value, values, label) => {
    if (!values.includes(value)) {
        throw new SettlementContractError(`${label} has an unknown value`);
    }
    return value;
};

const stringArray = (value, label) => {
    if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
        throw new SettlementContractError(`${label} must be an array of strings`);
    }
    return [...value];
};

const pageValue = (value, label) => {
    if (value === null) return null;
    if (typeof value !== "string" || !value.trim()) {
        throw new SettlementContractError(`${label} must be a page URL or null`);
    }

    let url;
    try {
        url = new URL(value, "https://settlements.invalid");
    } catch (_error) {
        throw new SettlementContractError(`${label} must be a valid page URL`);
    }
    if (!url.pathname.endsWith("/api/v2/admin/settlements")) {
        throw new SettlementContractError(`${label} must target the settlement list`);
    }
    const pages = url.searchParams.getAll("page");
    if (pages.length !== 1 || !/^[1-9]\d*$/.test(pages[0])) {
        throw new SettlementContractError(`${label} must contain one positive page`);
    }
    return Number(pages[0]);
};

const normalizeSettlement = (value) => {
    const statement = object(value, "settlement");
    return {
        id: positiveInteger(field(statement, "id"), "id"),
        company_id: positiveInteger(field(statement, "company_id"), "company_id"),
        company_name: string(field(statement, "company_name"), "company_name"),
        company_legal_name: string(field(statement, "company_legal_name"), "company_legal_name"),
        company_bin_number: string(field(statement, "company_bin_number"), "company_bin_number"),
        company_account_number: string(field(statement, "company_account_number"), "company_account_number"),
        branch_id: nullablePositiveInteger(field(statement, "branch_id"), "branch_id"),
        branch_name:
            field(statement, "branch_name") === null
                ? null
                : string(statement.branch_name, "branch_name"),
        settlement_scope: enumValue(
            field(statement, "settlement_scope"),
            ["company", "branch"],
            "settlement_scope",
        ),
        status: enumValue(field(statement, "status"), SETTLEMENT_STATUSES, "status"),
        period_start: nullableTimestamp(field(statement, "period_start"), "period_start"),
        period_end: nullableTimestamp(field(statement, "period_end"), "period_end"),
        gross_amount: money(field(statement, "gross_amount"), "gross_amount"),
        commission_amount: money(field(statement, "commission_amount"), "commission_amount"),
        payable_amount: money(field(statement, "payable_amount"), "payable_amount"),
        adjustment_amount: money(field(statement, "adjustment_amount"), "adjustment_amount"),
        purchase_count: nonNegativeInteger(field(statement, "purchase_count"), "purchase_count"),
        refund_count: nonNegativeInteger(field(statement, "refund_count"), "refund_count"),
        readiness_status: enumValue(
            field(statement, "readiness_status"),
            SETTLEMENT_READINESS_STATUSES,
            "readiness_status",
        ),
        readiness_reasons: stringArray(
            field(statement, "readiness_reasons"),
            "readiness_reasons",
        ),
        snapshot: object(field(statement, "snapshot"), "snapshot"),
        approved_at: nullableTimestamp(field(statement, "approved_at"), "approved_at"),
        exported_at: nullableTimestamp(field(statement, "exported_at"), "exported_at"),
        sent_to_bank_at: nullableTimestamp(
            field(statement, "sent_to_bank_at"),
            "sent_to_bank_at",
        ),
        paid_at: nullableTimestamp(field(statement, "paid_at"), "paid_at"),
        cancelled_at: nullableTimestamp(field(statement, "cancelled_at"), "cancelled_at"),
        cancel_reason: string(field(statement, "cancel_reason"), "cancel_reason"),
        created_at: timestamp(field(statement, "created_at"), "created_at"),
    };
};

const normalizeLine = (value, index) => {
    const label = `lines[${index}]`;
    const line = object(value, label);
    return {
        id: positiveInteger(field(line, "id", `${label}.id`), `${label}.id`),
        line_type: nonEmptyString(field(line, "line_type", `${label}.line_type`), `${label}.line_type`),
        branch_id: positiveInteger(field(line, "branch_id", `${label}.branch_id`), `${label}.branch_id`),
        branch_name: string(field(line, "branch_name", `${label}.branch_name`), `${label}.branch_name`),
        pay_bill_id:
            field(line, "pay_bill_id", `${label}.pay_bill_id`) === null
                ? null
                : string(line.pay_bill_id, `${label}.pay_bill_id`),
        accounting_transaction_id: nullablePositiveInteger(
            field(line, "accounting_transaction_id", `${label}.accounting_transaction_id`),
            `${label}.accounting_transaction_id`,
        ),
        source_table: nonEmptyString(field(line, "source_table", `${label}.source_table`), `${label}.source_table`),
        source_id: nonEmptyString(field(line, "source_id", `${label}.source_id`), `${label}.source_id`),
        gross_amount: money(field(line, "gross_amount", `${label}.gross_amount`), `${label}.gross_amount`),
        commission_amount: money(field(line, "commission_amount", `${label}.commission_amount`), `${label}.commission_amount`),
        payable_amount: money(field(line, "payable_amount", `${label}.payable_amount`), `${label}.payable_amount`),
        main_amount: money(field(line, "main_amount", `${label}.main_amount`), `${label}.main_amount`),
        points_amount: money(field(line, "points_amount", `${label}.points_amount`), `${label}.points_amount`),
        cashup_amount: money(field(line, "cashup_amount", `${label}.cashup_amount`), `${label}.cashup_amount`),
        off_percent: decimal4(field(line, "off_percent", `${label}.off_percent`), `${label}.off_percent`),
        occurred_at: nullableTimestamp(field(line, "occurred_at", `${label}.occurred_at`), `${label}.occurred_at`),
    };
};

export const normalizeSettlementList = (value) => {
    const page = object(value, "settlement page");
    const results = field(page, "results");
    if (!Array.isArray(results)) throw new SettlementContractError("results must be an array");
    return {
        count: nonNegativeInteger(field(page, "count"), "count"),
        next: pageValue(field(page, "next"), "next"),
        previous: pageValue(field(page, "previous"), "previous"),
        results: results.map(normalizeSettlement),
    };
};

export const normalizeSettlementDetail = (value) => {
    const statement = object(value, "settlement detail");
    const lines = field(statement, "lines");
    if (!Array.isArray(lines)) throw new SettlementContractError("lines must be an array");
    return {...normalizeSettlement(statement), lines: lines.map(normalizeLine)};
};

const normalizeSummary = (value) => {
    const summary = object(value, "summary");
    return {
        total_count: nonNegativeInteger(field(summary, "total_count"), "total_count"),
        ready_count: nonNegativeInteger(field(summary, "ready_count"), "ready_count"),
        not_ready_count: nonNegativeInteger(field(summary, "not_ready_count"), "not_ready_count"),
        total_payable_amount: money(field(summary, "total_payable_amount"), "total_payable_amount"),
        ready_payable_amount: money(field(summary, "ready_payable_amount"), "ready_payable_amount"),
    };
};

const successfulEnvelope = (value, label) => {
    const envelope = object(value, label);
    if (field(envelope, "success") !== true) {
        throw new SettlementContractError(`${label}.success must be true`);
    }
    return envelope;
};

export const normalizeSettlementReport = (value) => {
    const report = successfulEnvelope(value, "settlement report");
    const statements = field(report, "statements");
    if (!Array.isArray(statements)) throw new SettlementContractError("statements must be an array");
    return {
        success: true,
        summary: normalizeSummary(field(report, "summary")),
        statements: statements.map(normalizeSettlement),
    };
};

export const normalizeSettlementDraft = (value) => {
    const draft = successfulEnvelope(value, "settlement draft");
    const statements = field(draft, "statements");
    if (!Array.isArray(statements)) throw new SettlementContractError("statements must be an array");
    return {success: true, statements: statements.map(normalizeSettlement)};
};

export const normalizeSettlementMutation = (value) => {
    const mutation = successfulEnvelope(value, "settlement mutation");
    return {
        success: true,
        settlement: normalizeSettlementDetail(field(mutation, "settlement")),
    };
};

export const normalizeSettlementPayouts = (value) => {
    if (!Array.isArray(value)) throw new SettlementContractError("payouts must be an array");
    return value.map((entry, index) => {
        const label = `payouts[${index}]`;
        const payout = object(entry, label);
        return {
            id: positiveInteger(field(payout, "id", `${label}.id`), `${label}.id`),
            amount: money(field(payout, "amount", `${label}.amount`), `${label}.amount`),
            status: nonEmptyString(field(payout, "status", `${label}.status`), `${label}.status`),
            payment_reference: string(
                field(payout, "payment_reference", `${label}.payment_reference`),
                `${label}.payment_reference`,
            ),
            paid_at: nullableTimestamp(field(payout, "paid_at", `${label}.paid_at`), `${label}.paid_at`),
            paid_by_id: nullablePositiveInteger(
                field(payout, "paid_by_id", `${label}.paid_by_id`),
                `${label}.paid_by_id`,
            ),
        };
    });
};
